import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { KpiIds } from '@core/models/kpi.model';
import { ICONS } from '@core/services/icons/icons.service';

import { ButtonComponent } from '@shared/components/button/button.component';
import { KpiDetailPopupService } from '@shared/services/kpi-detail-popup/kpi-detail-popup.service';

import {
  ContextMenuComponent,
  ContextMenuOption
} from '../context-menu/context-menu.component';
import { SelectValue } from '../select/select-item.model';
import { SelectComponent } from '../select/select.component';
import {
  KpiCompareMode,
  KpiComparisonCell,
  KpiComparisonChoice,
  KpiComparisonGroup,
  KpiComparisonItem,
  KpiComparisonRow,
  buildComparisonGroups
} from './kpi-comparison.model';

interface ModeOption {
  readonly id: KpiCompareMode;
  readonly labelKey: string;
}

@Component({
  selector: 'app-kpi-comparison',
  imports: [TranslatePipe, ContextMenuComponent, ButtonComponent, SelectComponent],
  templateUrl: './kpi-comparison.component.html',
  host: { class: 'bg-elevated flex h-full w-full flex-col overflow-hidden' },
  styles: ``
})
export class KpiComparisonComponent {
  readonly items = input.required<KpiComparisonItem[]>();
  readonly decimals = input(1);
  readonly kpiOrder = input<KpiIds[]>([]);
  readonly closed = output<void>();
  readonly choiceChange = output<KpiComparisonChoice>();

  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((event) => event.lang)),
    { initialValue: this.translate.getCurrentLang() }
  );

  private readonly kpiDetailPopup = inject(KpiDetailPopupService);

  protected readonly ICONS = ICONS;

  protected readonly MODES: ModeOption[] = [
    { id: 'abs', labelKey: 'features.kpi_comparison.mode.absolute' },
    { id: 'delta', labelKey: 'features.kpi_comparison.mode.delta' },
    { id: 'pct', labelKey: 'features.kpi_comparison.mode.percent' }
  ];

  protected readonly mode = signal<KpiCompareMode>('abs');
  protected readonly referenceId = signal<string | null>(null);
  /** The column the user singled out: the others dim rather than disappear. */
  protected readonly focusId = signal<string | null>(null);

  protected readonly menuOpen = signal(false);
  protected readonly menuX = signal(0);
  protected readonly menuY = signal(0);

  constructor() {
    // A reference or a focus on a row the user has just deselected would otherwise leave the
    // panel comparing against a column that is no longer on screen.
    effect(() => {
      const ids = new Set(this.items().map((item) => item.id));
      untracked(() => {
        const reference = this.referenceId();
        if (reference !== null && !ids.has(reference)) {
          this.referenceId.set(null);
          this.mode.set('abs');
        }
        const focus = this.focusId();
        if (focus !== null && !ids.has(focus)) this.focusId.set(null);
      });
    });
  }

  protected readonly groups = computed<KpiComparisonGroup[]>(() =>
    buildComparisonGroups(this.items(), {
      mode: this.mode(),
      referenceId: this.referenceId(),
      decimals: this.decimals(),
      kpiOrder: this.kpiOrder()
    })
  );

  protected readonly reference = computed<KpiComparisonItem | null>(
    () => this.items().find((item) => item.id === this.referenceId()) ?? null
  );

  /**
   * Columns narrow past five so a six-plan comparison still fits a laptop without horizontal
   * scrolling — which is where the sticky label column earns its keep when it does not.
   */
  protected readonly gridTemplate = computed(() => {
    const count = this.items().length;
    const width = count > 5 ? 128 : 152;
    return `208px repeat(${count}, minmax(${width}px, 1fr))`;
  });

  /** Distinct KPIs across the selection, which is the row count the summary reports. */
  protected readonly kpiCount = computed<number>(() => {
    const ids = new Set<KpiIds>();
    for (const item of this.items()) {
      for (const kpi of item.kpis) ids.add(kpi.id);
    }
    return ids.size;
  });

  protected readonly menuOptions = computed<ContextMenuOption[]>(() => {
    this.currentLang();
    const current = this.referenceId();
    return [
      {
        label: this.translate.instant('features.kpi_comparison.reference.none'),
        icon: current === null ? ICONS.check : undefined,
        action: () => {
          this.referenceId.set(null);
          this.mode.set('abs');
        }
      },
      ...this.items().map((item) => ({
        label: item.label,
        icon: current === item.id ? ICONS.check : undefined,
        action: () => {
          this.referenceId.set(item.id);
          if (this.mode() === 'abs') this.mode.set('delta');
        }
      }))
    ];
  });

  protected openMenu(trigger: ButtonComponent): void {
    const rect = trigger.elementRef.nativeElement.getBoundingClientRect();
    this.menuX.set(rect.left);
    this.menuY.set(rect.bottom + 4);
    this.menuOpen.update((open) => !open);
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  /** Switching to a relative mode with no reference yet adopts the leftmost column. */
  protected setMode(mode: KpiCompareMode): void {
    if (mode !== 'abs' && this.referenceId() === null) {
      const first = this.items()[0];
      if (!first) return;
      this.referenceId.set(first.id);
    }
    this.mode.set(mode);
  }

  protected toggleFocus(itemId: string): void {
    this.focusId.update((current) => (current === itemId ? null : itemId));
  }

  protected choose(itemId: string, value: SelectValue): void {
    if (typeof value === 'string' && value !== '')
      this.choiceChange.emit({ itemId, choiceId: value });
  }

  protected isDimmed(itemId: string): boolean {
    const focus = this.focusId();
    return focus !== null && focus !== itemId;
  }

  protected toggleDetail(
    row: KpiComparisonRow,
    cell: KpiComparisonCell,
    cellEl: HTMLElement
  ): void {
    if (cell.detail.length === 0) return;
    this.kpiDetailPopup.toggle(cellEl, {
      labelKey: row.labelKey,
      rows: cell.detail,
      placement: 'top'
    });
  }

  protected onCellKeydown(
    row: KpiComparisonRow,
    cell: KpiComparisonCell,
    event: Event
  ): void {
    event.preventDefault();
    this.toggleDetail(row, cell, event.currentTarget as HTMLElement);
  }
}
